/**
 * THE "HOW THIS WORKS" PANELS, ALL IN ONE PLACE — owner, 2026-10-04.
 *
 * Screens carry no guidance text of their own (CLAUDE.md, "Simple outweighs complex"). What a
 * director might genuinely want to know — a rule the screen cannot show — lives here, behind the ⓘ
 * beside a heading, in a few short lines. One file, so the wording stays consistent and Courtney can
 * change any of it in one place. Much of it is her own wording, moved off the screens.
 *
 * Rule for adding a line: it must say something the screen does not already show. Never explain a
 * button or a field.
 */

import type { Tiebreaker } from '@league/shared/scoring';

export type ScoringMode = 'social' | 'tournament' | undefined;

export interface HowTo {
  title: string;
  lines: string[];
}

export function scoringHowTo(mode: ScoringMode): HowTo {
  return {
    title: 'How scoring works',
    lines: [mode === 'tournament'
      ? 'The highest four non-negative game earnings count, and all deductions still apply.'
      : mode === 'social'
        ? 'The highest four game earnings count toward the session score. There are no negative deductions.'
        : 'The highest four game scores count toward the session score.',
      'Incomplete games do not count as completed games.'],
  };
}

export function seatingHowTo(): HowTo {
  return {
    title: 'How seating works',
    lines: [
      'Seating keeps players who asked to sit together at one table, then seats players of a similar pace together.',
      'Mark anyone away first. They are left out, and a sub can take their seat.',
      'Move anyone by dragging them to a seat, or tap their name, then a seat.',
      'Finalize when you are done. Players see their table when they sign in. No email is sent.',
    ],
  };
}

/**
 * HOW TIES ARE DECIDED, SAID THE WAY THE STANDINGS ENGINE ACTUALLY DECIDES THEM — owner, 2026-10-11:
 * the old lines were jargon and said nothing for leagues without a scoring mode. Built from the same
 * rules `computeStandings` applies (packages/shared/src/scoring/standings.ts):
 *   - with a scoring mode: biggest single winning hand, then games won, then (Competitive) fewer
 *     penalty points — `compareLeagueEvidence`;
 *   - without one: the league's own tiebreakers, in order. 'point_differential' decides nothing yet,
 *     so it is not described.
 * Players still level share the place and the next place is skipped (1, 1, 3).
 */
const DEFAULT_TIEBREAKERS: Tiebreaker[] = ['wins', 'head_to_head', 'point_differential'];
const TIEBREAKER_WORDS: Partial<Record<Tiebreaker, string>> = {
  wins: 'the one who won more games goes ahead.',
  head_to_head: 'the one who scored more in the sessions they both played goes ahead.',
  sessions_played: 'the one who played more sessions goes ahead.',
};

export function tieRules(mode: ScoringMode, tiebreakers: Tiebreaker[] = DEFAULT_TIEBREAKERS): string[] {
  const steps = mode
    ? [
        'the one with the bigger single winning hand (bonuses included) goes ahead.',
        'the one who won more games goes ahead.',
        ...(mode === 'tournament' ? ['the one with fewer penalty points goes ahead.'] : []),
      ]
    : tiebreakers.flatMap((tiebreaker) => (TIEBREAKER_WORDS[tiebreaker] ? [TIEBREAKER_WORDS[tiebreaker]!] : []));
  return [
    'Players are ranked by total points.',
    ...steps.map((step, index) => (index === 0 ? `If two players have the same total, ${step}` : `If that’s equal too, ${step}`)),
    'Players still level share the place, and the next place is skipped (1, 1, 3).',
  ];
}

export function standingsHowTo(mode: ScoringMode, tiebreakers?: Tiebreaker[]): HowTo {
  return { title: 'How ties are decided', lines: tieRules(mode, tiebreakers) };
}
